import HttpClient from "../../utils/http";
import {
  rewards_listing,
  rewards_redeem,
  rewards_redemption_history,
  rewards_redemption,
  rewards_redemption_requests,
  rewards_user,
} from "../constants/api_endpoints";

export interface RewardsListFilters {
  page: number;
  size: number;
  keyword: string;
  rewardFrom: number | null;
  rewardTo: number | null;
  startDate: string | null;
  endDate: string | null;
  ageFrom: number | null;
  ageTo: number | null;
  area: string | null;
  genderId: number | null;
  stateId: number | null;
  district: number | null;
  orgId?: number | null;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export function getRewardsList(filters: RewardsListFilters) {
  const params = new URLSearchParams({
    page: filters.page.toString(),
    size: filters.size.toString(),
  });
  if (filters.keyword) params.append("keyword", filters.keyword);
  if (filters.rewardFrom !== null && filters.rewardTo !== null) {
    params.append("from", filters.rewardFrom.toString());
    params.append("to", filters.rewardTo.toString());
  }
  if (filters.startDate && filters.endDate) {
    params.append("dateFrom", filters.startDate);
    params.append("dateTo", filters.endDate);
  }
  if (filters.ageFrom !== null && filters.ageTo !== null) {
    params.append("ageFrom", filters.ageFrom.toString());
    params.append("ageTo", filters.ageTo.toString());
  }
  if (filters.area) params.append("area", filters.area);
  if (filters.genderId !== null) params.append("gender_id", filters.genderId.toString());
  if (filters.stateId !== null) params.append("state", filters.stateId.toString());
  if (filters.district !== null) params.append("district", filters.district.toString());
  if (filters.orgId) params.append("orgId", filters.orgId.toString());
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
  return HttpClient.get(`${rewards_listing}?${params.toString()}`);
}

export function getRewardsListById({ userId }: { userId: number }) {
  return HttpClient.get(`${rewards_user}/${userId}`);
}

export function getRewardsStatement({
  userId,
  page,
  size,
}: {
  userId: number;
  page: number;
  size: number;
}) {
  return HttpClient.get(`${rewards_user}/${userId}/statement?page=${page}&size=${size}`);
}

export function redemption({
  userId,
  points,
  comments,
}: {
  userId: number;
  points: number;
  comments: string;
}) {
  return HttpClient.post(`${rewards_redeem}/${userId}/redemption`, {
    points,
    comments,
  });
}

export function getRedemptionHistory({
  userId,
  page,
  size,
}: {
  userId: number;
  page: number;
  size: number;
}) {
  return HttpClient.get(`${rewards_redemption_history}/${userId}/redemptions?page=${page}&size=${size}`);
}

export function updateRedemptionComments({
  redemptionId,
  comments,
}: {
  redemptionId: number;
  comments: string;
}) {
  return HttpClient.patch(`${rewards_redemption}/${redemptionId}`, {
    comments,
  });
}

// --- User-requested rewards (redemption requests raised from the app) -----

export interface RedemptionRequestFilters {
  page: number;
  size: number;
  status?: string | null;
  payoutStatus?: string | null;
  orgId?: number | null;
}

export function getRedemptionRequests(filters: RedemptionRequestFilters) {
  const params = new URLSearchParams({
    page: filters.page.toString(),
    size: filters.size.toString(),
  });
  if (filters.status) params.append("status", filters.status);
  if (filters.payoutStatus) params.append("payoutStatus", filters.payoutStatus);
  if (filters.orgId) params.append("orgId", filters.orgId.toString());
  return HttpClient.get(`${rewards_redemption_requests}?${params.toString()}`);
}

export function reviewRedemptionRequest({
  requestId,
  status,
  comments,
}: {
  requestId: number;
  status: "In Progress" | "Rejected";
  comments: string;
}) {
  return HttpClient.patch(`${rewards_redemption_requests}/${requestId}`, {
    status,
    comments,
  });
}

export function updateRedemptionPayout({
  requestId,
  payoutStatus,
  payoutReference,
  payoutRemarks,
}: {
  requestId: number;
  payoutStatus: "Processing" | "Released" | "Failed";
  payoutReference?: string;
  payoutRemarks?: string;
}) {
  return HttpClient.patch(`${rewards_redemption_requests}/payout/${requestId}`, {
    payoutStatus,
    ...(payoutReference ? { payoutReference } : {}),
    ...(payoutRemarks ? { payoutRemarks } : {}),
  });
}
